import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import React, { FC, useContext, useRef, useState } from 'react'
import { useCookies } from 'react-cookie'

import { Button } from '@Components/Button'
import { Wordbook } from '@Components/Wordbook'
import { PageType, useReturnOptionsByEmailQuery } from '@Generated/graphql'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { MetadataContext } from '@Globals/Metadata'
import { useTranslation } from '@Globals/Translations'
import { EmailRegexValidation } from '@Shared/Components/Forms/Validations/EmailValidation'
import { TextInput, TextInputActionProps } from '@Shared/Components/Inputs/TextInput/TextInput'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './ReturnLogin.module.scss'

export interface ReturnLoginProps {
  className?: string
}

const RETURN_COOKIE = 'th_returning_order'

export const ReturnLogin: FC<ReturnLoginProps> = ({ className }) => {
  const { t } = useTranslation()
  const { storeURL } = useContext(MetadataContext)
  const [orderNotFoundError, setOrderNotFoundError] = useState(false)
  const [orderExpiredError, setOrderExpiredError] = useState(false)
  const [, setCookie] = useCookies([RETURN_COOKIE])
  const [orderNumber, setOrderNumber] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const emailActionRef = useRef<TextInputActionProps | null>(null)
  const orderNumberActionRef = useRef<TextInputActionProps | null>(null)
  const openModal = useCMSModalOpen()
  const { loading, refetch } = useReturnOptionsByEmailQuery({
    context: {
      domainProxy: true,
      userSpecific: true,
      ssr: false,
    },
    fetchPolicy: 'standby',
    nextFetchPolicy: 'standby',
    skip: false,
  })

  return (
    <form
      className={clsx(styles.returnLogin, className)}
      onSubmit={(event) => {
        event.preventDefault()
        const isEmailValid = emailActionRef.current?.validate()
        const isOrderNumberValid = orderNumberActionRef.current?.validate()
        if (isEmailValid && isOrderNumberValid) {
          setOrderNotFoundError(false)
          setOrderExpiredError(false)
          refetch({
            orderNumber,
            email,
          }).then((response) => {
            if ((response?.errors?.length || 0) > 0) {
              throw new Error(first(response.errors)?.message)
            }
            const dataOrderNumber = response?.data?.returnOptionsByEmail?.orderNumber

            if (dataOrderNumber) {
              setCookie(RETURN_COOKIE, JSON.stringify({
                orderNumber: dataOrderNumber,
                email,
              }))
              window.location.href = storeURL(`returns/${dataOrderNumber}`)
            }
          }).catch((error) => {
            if (error.message === '9 FAILED_PRECONDITION: return right expired') {
              setOrderExpiredError(true)
              return
            }
            setOrderNotFoundError(true)
          })
        }
      }}
    >
      <div className={clsx(styles.header, (orderNotFoundError || orderExpiredError) && styles.error)}>
        <h1 className={styles.title}>{t('return:RETURN_FLOW_LOGIN_TITLE')}</h1>
        <span className={styles.subTitle}>{t('return:RETURN_LOGIN_DESCRIPTION')}</span>
      </div>
      <div className={styles.limitWidth}>
        <label className={styles.label} htmlFor="return-login-order-number-input">{t('return:ORDER_NUMBER_LABEL')}</label>
        <span>{t('return:RETURN_LOGIN_ORDER_NUMBER_FIELD_TEXT')}</span>
        <TextInput
          actionRef={orderNumberActionRef}
          label={t('return:ORDER_NUMBER_LABEL')}
          onChange={o => setOrderNumber(o.trim())}
          validations={[
            value => ({
              errorMessage: t('cms:COF_YOU_ARE_MISSING_THIS_FIELD'),
              success: !isEmpty(value),
            }),
          ]}
          value={orderNumber}
        />
      </div>
      <div className={styles.limitWidth}>
        <label className={styles.label} htmlFor="return-login-email-input">{t('common:EMAIL_LABEL')}</label>
        <span>{t('return:RETURN_LOGIN_EMAIL_FIELD_TEXT')}</span>
        <TextInput
          actionRef={emailActionRef}
          label={t('common:EMAIL_LABEL')}
          onChange={e => setEmail(e.trim())}
          validations={[
            value => ({
              errorMessage: t('validation:YOUR_EMAIL_IS_INVALID'),
              success: EmailRegexValidation(value),
            }),
          ]}
          value={email}
        />
      </div>
      <Button className={clsx(styles.limitWidth, styles.button)} disabled={!EmailRegexValidation(email) || isEmpty(orderNumber)} type="submit">
        {!loading && t('return:NEXT_LABEL')}
        {loading && <Spinner className={styles.spinner} />}
      </Button>
      <div className={clsx(styles.bigError, styles.expiredError)}>
        {orderNotFoundError && t('return:RETURN_LOGIN_NOT_FOUND')}
        {orderExpiredError && (
          <Wordbook
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            components={{ Link: <button onClick={() => openModal(PageType.Modal, 'contact-us')} type="button" /> }}
            wordbook={{ defaults: t('return:RETURN_LOGIN_ERROR_EXPIRATION') }}
          />
        )}
      </div>
    </form>
  )
}

